/**
 * GridDensityContext - 帖子网格显示密度管理
 * 用于控制内容区 post grid 的显示密度
 */

import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

// 密度档位：1=紧凑(158px), 2=标准(180px), 3=宽松(208px)
export type GridDensityLevel = 1 | 2 | 3;

interface GridDensityContextValue {
  density: GridDensityLevel;
  setDensity: (level: GridDensityLevel) => void;
}

const GridDensityContext = createContext<GridDensityContextValue | undefined>(undefined);

const STORAGE_KEY = 'grid-density';

// 密度对应的 CSS 值
const DENSITY_VALUES: Record<GridDensityLevel, string> = {
  1: '152px',
  2: '180px',
  3: '216px',
};

export const GridDensityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [density, setDensityState] = useState<GridDensityLevel>(() => {
    // 从 localStorage 读取初始值
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (parsed >= 1 && parsed <= 3) {
        return parsed as GridDensityLevel;
      }
    }
    return 2; // 默认标准密度
  });

  // 更新 CSS 变量
  useEffect(() => {
    document.documentElement.style.setProperty('--grid-min-width', DENSITY_VALUES[density]);
  }, [density]);

  const setDensity = (level: GridDensityLevel) => {
    setDensityState(level);
    localStorage.setItem(STORAGE_KEY, String(level));
  };

  return (
    <GridDensityContext.Provider value={{ density, setDensity }}>
      {children}
    </GridDensityContext.Provider>
  );
};

export const useGridDensity = (): GridDensityContextValue => {
  const context = useContext(GridDensityContext);
  if (!context) {
    throw new Error('useGridDensity must be used within a GridDensityProvider');
  }
  return context;
};
